import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Languages, RefreshCw, Check, AlertCircle, Zap, BookOpen, Play } from "lucide-react";

/**
 * Admin: UI translations health & re-sync.
 * - Shows per-language key coverage vs en.json.
 * - "Re-sync missing" translates only missing/new keys.
 * - "Force re-sync all" retranslates every value (destructive to manual tweaks).
 */
export default function AdminTranslations() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [error, setError] = useState(null);

  // Wiki content translation state (separate pipeline from UI strings)
  const [wikiStatus, setWikiStatus] = useState(null);
  const [wikiLoading, setWikiLoading] = useState(true);
  const [wikiRunning, setWikiRunning] = useState(false);
  const [wikiError, setWikiError] = useState(null);

  const loadWikiStatus = async () => {
    setWikiLoading(true);
    setWikiError(null);
    try {
      const r = await client.get("/wiki/translate/status");
      setWikiStatus(r.data);
    } catch (e) {
      setWikiError(e?.response?.data?.detail || e.message || "Failed to load wiki status");
    } finally {
      setWikiLoading(false);
    }
  };

  const runWikiSync = async (force) => {
    const msg = force
      ? "FORCE re-translate every wiki article into every language? This overwrites any manual edits and burns significant LLM quota."
      : "Translate all wiki articles into every missing language? Runs in the background — status will update as pages complete.";
    if (!window.confirm(msg)) return;
    setWikiRunning(true);
    setWikiError(null);
    try {
      await client.post(`/wiki/translate?force=${force ? "true" : "false"}`);
      // Poll the status a few times to reflect early progress
      for (let i = 0; i < 6; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        await loadWikiStatus();
      }
    } catch (e) {
      setWikiError(e?.response?.data?.detail || e.message || "Wiki translation kickoff failed");
    } finally {
      setWikiRunning(false);
    }
  };

  const loadStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await client.get("/admin/ui-translations/status");
      setStatus(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); loadWikiStatus(); }, []);

  const runResync = async (force) => {
    const label = force ? "FORCE re-translate ALL keys in every language (this overwrites any manual edits and consumes significantly more LLM quota). Continue?" : "Re-sync missing translation keys across all 9 non-English languages?";
    if (!window.confirm(label)) return;
    setRunning(true);
    setError(null);
    setLastRun(null);
    try {
      const r = await client.post(`/admin/ui-translations/resync?force=${force ? "true" : "false"}`);
      setLastRun(r.data);
      await loadStatus();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Re-sync failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div data-testid="admin-translations-tab">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display text-xl md:text-2xl text-[var(--t-text)] uppercase flex items-center gap-2">
            <Languages className="w-5 h-5 text-[var(--t-blue-glow)]" /> UI Translations
          </h2>
          <p className="text-xs md:text-sm text-[var(--t-text-dim)] mt-1 max-w-3xl">
            Coverage of every non-English locale bundle vs. <span className="font-mono text-[var(--t-cyan)]">en.json</span>. Missing keys are auto-translated via Claude Sonnet 4.6.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={loadStatus}
            className="btn-ghost !text-xs"
            data-testid="translations-refresh-status"
            disabled={loading || running}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={() => runResync(false)}
            className="btn-primary !text-xs"
            data-testid="translations-resync-missing"
            disabled={running || loading}
          >
            <Zap className={`w-3.5 h-3.5 ${running ? "animate-pulse" : ""}`} /> {running ? "Syncing…" : "Re-sync missing"}
          </button>
          <button
            onClick={() => runResync(true)}
            className="btn-ghost !text-xs !border-[var(--t-red)] !text-[var(--t-red)] hover:!bg-[rgba(220,80,80,0.06)]"
            data-testid="translations-force-resync"
            disabled={running || loading}
          >
            Force re-sync ALL
          </button>
        </div>
      </div>

      {error && (
        <div className="panel border-[var(--t-red)] p-3 mb-4 flex items-start gap-2 text-sm" data-testid="translations-error">
          <AlertCircle className="w-4 h-4 text-[var(--t-red)] flex-shrink-0 mt-0.5" />
          <span className="text-[var(--t-red)] font-mono text-xs">{error}</span>
        </div>
      )}

      {lastRun && (
        <div className="panel corner-frame p-3 mb-4 text-sm" data-testid="translations-last-run">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--t-cyan)] mb-1">// Last run</div>
          <div className="text-[var(--t-text-dim)]">
            {lastRun.total_keys_translated} keys translated across {lastRun.languages_updated} language(s) ·
            <span className="text-[var(--t-text-muted)] font-mono ml-1">force={String(lastRun.force)}</span>
          </div>
        </div>
      )}

      {/* Language grid */}
      {loading && !status ? (
        <div className="font-mono text-xs tracking-[0.3em] text-[var(--t-blue-glow)] uppercase pulse-glow py-8 text-center">
          // loading locale status...
        </div>
      ) : status ? (
        <>
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--t-text-muted)] mb-3">
            // english source · <span className="text-[var(--t-cyan)]">{status.total_keys}</span> keys
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="translations-lang-grid">
            {status.languages.map((l) => {
              const pct = status.total_keys > 0 ? Math.round((l.translated / status.total_keys) * 100) : 0;
              const complete = l.missing === 0;
              const color = complete ? "#5fd99a" : pct >= 90 ? "#f3c552" : "#e35a5a";
              return (
                <div
                  key={l.code}
                  className="panel p-4 relative overflow-hidden"
                  data-testid={`translations-lang-${l.code}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-display text-base text-[var(--t-text)] uppercase">{l.name}</span>
                      <span className="font-mono text-[10px] tracking-[0.2em] text-[var(--t-text-muted)]">{l.code}</span>
                    </div>
                    {complete ? (
                      <Check className="w-4 h-4" style={{ color }} strokeWidth={2.4} />
                    ) : (
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color }}>
                        {l.missing} missing
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between mb-1.5 text-[10px] font-mono uppercase tracking-[0.2em]">
                    <span className="text-[var(--t-text-dim)]">
                      {l.translated} / {l.total}
                    </span>
                    <span style={{ color }}>{pct}%</span>
                  </div>
                  <div className="h-1 w-full bg-[var(--t-panel-2)] border border-[var(--t-border)] overflow-hidden">
                    <div
                      className="h-full transition-all duration-500"
                      style={{ width: `${pct}%`, background: color, boxShadow: `0 0 8px ${color}80` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-6 text-[11px] text-[var(--t-text-muted)] font-mono leading-relaxed">
            // Locale JSON files live in <span className="text-[var(--t-cyan)]">frontend/src/i18n/locales/</span>. Preview hot-reloads immediately after a re-sync.
            Deploy to push updates to <span className="text-[var(--t-cyan)]">territoria.wiki</span>.
          </p>
        </>
      ) : null}

      {/* ─────────────  WIKI CONTENT TRANSLATION  ───────────── */}
      <div className="mt-12 pt-8 border-t border-[var(--t-border)]" data-testid="wiki-content-translation-section">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
          <div>
            <h2 className="font-display text-xl md:text-2xl text-[var(--t-text)] uppercase flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[var(--t-blue-glow)]" /> Wiki Content
            </h2>
            <p className="text-xs md:text-sm text-[var(--t-text-dim)] mt-1 max-w-3xl">
              Translate every wiki article body + title + summary into all 9 languages. Runs as a background job on the server — DB-resident, so it survives redeploys and hot-reloads work instantly for viewers.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={loadWikiStatus}
              className="btn-ghost !text-xs"
              disabled={wikiLoading || wikiRunning}
              data-testid="wiki-refresh-status"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${wikiLoading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={() => runWikiSync(false)}
              className="btn-primary !text-xs"
              disabled={wikiRunning || wikiLoading}
              data-testid="wiki-translate-missing"
            >
              <Play className={`w-3.5 h-3.5 ${wikiRunning ? "animate-pulse" : ""}`} /> {wikiRunning ? "Translating…" : "Translate missing"}
            </button>
            <button
              onClick={() => runWikiSync(true)}
              className="btn-ghost !text-xs !border-[var(--t-red)] !text-[var(--t-red)] hover:!bg-[rgba(220,80,80,0.06)]"
              disabled={wikiRunning || wikiLoading}
              data-testid="wiki-force-translate"
            >
              Force re-translate ALL
            </button>
          </div>
        </div>

        {wikiError && (
          <div className="panel border-[var(--t-red)] p-3 mb-4 flex items-start gap-2 text-sm" data-testid="wiki-translations-error">
            <AlertCircle className="w-4 h-4 text-[var(--t-red)] flex-shrink-0 mt-0.5" />
            <span className="text-[var(--t-red)] font-mono text-xs">{wikiError}</span>
          </div>
        )}

        {wikiLoading && !wikiStatus ? (
          <div className="font-mono text-xs tracking-[0.3em] text-[var(--t-blue-glow)] uppercase pulse-glow py-8 text-center">
            // loading wiki status...
          </div>
        ) : wikiStatus ? (
          <>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--t-text-muted)] mb-3">
              // english source · <span className="text-[var(--t-cyan)]">{wikiStatus.coverage?.en || 0}</span> pages
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="wiki-lang-grid">
              {wikiStatus.languages
                .filter((l) => l.code !== "en")
                .map((l) => {
                  const total = wikiStatus.coverage?.en || 0;
                  const done = wikiStatus.coverage?.[l.code] || 0;
                  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                  const missing = Math.max(0, total - done);
                  const complete = missing === 0 && total > 0;
                  const job = wikiStatus.jobs?.[l.code];
                  const isRunning = job && job.status === "running";
                  const color = isRunning
                    ? "#f3c552"
                    : complete
                    ? "#5fd99a"
                    : pct >= 90
                    ? "#f3c552"
                    : "#e35a5a";
                  return (
                    <div
                      key={l.code}
                      className="panel p-4 relative overflow-hidden"
                      data-testid={`wiki-lang-${l.code}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-display text-base text-[var(--t-text)] uppercase">
                            {l.name}
                          </span>
                          <span className="font-mono text-[10px] tracking-[0.2em] text-[var(--t-text-muted)]">
                            {l.code}
                          </span>
                        </div>
                        {isRunning ? (
                          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#f3c552] pulse-glow">
                            translating…
                          </span>
                        ) : complete ? (
                          <Check className="w-4 h-4" style={{ color }} strokeWidth={2.4} />
                        ) : (
                          <span
                            className="font-mono text-[10px] uppercase tracking-[0.2em]"
                            style={{ color }}
                          >
                            {missing} missing
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between mb-1.5 text-[10px] font-mono uppercase tracking-[0.2em]">
                        <span className="text-[var(--t-text-dim)]">
                          {done} / {total}
                        </span>
                        <span style={{ color }}>{pct}%</span>
                      </div>
                      <div className="h-1 w-full bg-[var(--t-panel-2)] border border-[var(--t-border)] overflow-hidden">
                        <div
                          className="h-full transition-all duration-500"
                          style={{ width: `${pct}%`, background: color, boxShadow: `0 0 8px ${color}80` }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
            <p className="mt-6 text-[11px] text-[var(--t-text-muted)] font-mono leading-relaxed">
              // Article translations live in the <span className="text-[var(--t-cyan)]">wiki_pages</span> Mongo collection (keyed on slug+language).
              Missing translations silently fall back to the English source so the wiki is never broken.
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}
